import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableContainer,
  DataTableContainerContent,
  DataTableContainerFooter,
  DataTableHead,
  DataTableHeader,
  DataTablePagination,
  DataTableRow,
  useColumnVisibilityAndOrderState,
  useDataTableLoadingGuard,
  useDebounceEffect,
  useServerSorting,
} from '@autone/ui';
import { NoDataMessage } from '@autone/ui-next/components';
import { createPaginatedInfiniteQueryHook } from '@autone/utils';
import { PAGE_SIZE_OPTIONS } from '@autone/utils';
import { useNavigate } from '@tanstack/react-router';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

import { type APP_NAME } from '../../../consts';
import {
  useAssortmentAdvancedFilters,
  useAssortmentGeneralFilters,
} from '../../../hooks';
import {
  type GetAssortmentProductsRequest,
  type GetAssortmentProductsSortOption,
} from '../../../redux/assortment';
import { useGetPaginatedAssortmentProductsInfiniteQuery } from '../../../redux/assortment/assortment';
import { inSeasonSearchTermAtom } from '../../../storage/atoms';
import { useAssortmentSessionStorageAtom } from '../../../storage/session-storage';
import { trackAssortmentTableEvents } from '../analytics';
import { AssortmentTableContextProvider } from '../contexts';

import { productsTableColumnDefs } from './ProductsTable.columns';
import { type ProductsTableColumnId } from './ProductsTable.types';
import { sortableColumnToFieldIdMap } from './ProductsTable.utilities';

const usePaginatedGetAssortmentProductsQuery = createPaginatedInfiniteQueryHook(
  useGetPaginatedAssortmentProductsInfiniteQuery,
);

export const ProductsTable = () => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();

  const searchTerm = useAtomValue(inSeasonSearchTermAtom);
  const { selectedGeneralFiltersApi } =
    useAssortmentGeneralFilters('productsTable');
  const { selectedAdvancedFiltersApi } =
    useAssortmentAdvancedFilters('productsTable');

  const { sortBy, sortingState, setSortingState } = useServerSorting<
    GetAssortmentProductsSortOption,
    ProductsTableColumnId
  >('product_id', false, sortableColumnToFieldIdMap);

  const queryArgs = useMemo(() => {
    return {
      body: {
        filters: {
          ...selectedGeneralFiltersApi,
          product_search_term: searchTerm,
        },
        advanced_filter: selectedAdvancedFiltersApi,
      },
      query: { sort_by: sortBy },
    } satisfies GetAssortmentProductsRequest;
  }, [
    selectedGeneralFiltersApi,
    selectedAdvancedFiltersApi,
    searchTerm,
    sortBy,
  ]);

  const {
    currentPage,
    getNextPage,
    getPrevPage,
    setPageSize,
    paginationState: { pageIndex, canGetNextPage, canGetPrevPage, pageSize },
    infiniteQueryResults: { isFetching, isLoading, isUninitialized },
  } = usePaginatedGetAssortmentProductsQuery(queryArgs, {
    initialPageSize: PAGE_SIZE_OPTIONS[1],
    initialPageIndex: 0,
  });

  useDebounceEffect(
    () => {
      const { query, body } = queryArgs;
      const { sort_by } = query;
      const { filters, advanced_filter } = body;
      const filterDimensionsApplied = Object.keys(filters);
      const advancedFilterFieldsApplied = advanced_filter?.conditions.map(
        (condition) => condition.field,
      );
      trackAssortmentTableEvents({
        tableName: 'product_view_table',
        sortBy: sort_by,
        filterDimensionsApplied,
        advancedFilterFieldsApplied,
      });
    },
    [queryArgs],
    300,
  );

  const { memoisedData, getRowIdLoadingGuard, dataLoadingGuard } =
    useDataTableLoadingGuard({
      mode: 'dynamic',
      isLoading,
      isFetching,
      isUninitialized,
      data: currentPage?.items,
      initialRowCount: 50,
    });

  const totalRows = useMemo(
    () => (currentPage ? currentPage.page_info.total_count : 0),
    [currentPage],
  );

  const [customColumnsAtom] = useAssortmentSessionStorageAtom(
    'productsTable.extrasMenu.customColumns',
  );
  const { columnOrder, columnVisibility } = useColumnVisibilityAndOrderState(
    productsTableColumnDefs,
    customColumnsAtom,
  );

  const table = useReactTable({
    data: memoisedData,
    columns: productsTableColumnDefs,
    getRowId: getRowIdLoadingGuard((row) => row.product.id),
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSortingState,
    enableMultiSort: false,
    state: { sorting: sortingState, columnOrder, columnVisibility },
  });

  const hasVisibleRows = table.getRowModel().rows.length > 0;
  const shouldDisplayNoData = !hasVisibleRows && !isFetching;

  const navigate = useNavigate();
  const handleClickRow = useCallback(
    (productId: string) => {
      trackAnalyticsEvent({
        category: 'assortment_tables',
        action: 'product_view_table:row_clicked',
      });
      navigate({ to: '/product/$productId/locations', params: { productId } });
    },
    [navigate],
  );

  return (
    <AssortmentTableContextProvider table={'productsTable'}>
      <DataTableContainer className="rounded-none border-none">
        <DataTableContainerContent className="max-h-[70vh]">
          <DataTable
            showScrollbar={!shouldDisplayNoData}
            className="min-w-full table-fixed"
          >
            <DataTableHeader className="z-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <DataTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <DataTableHead
                      style={{ width: header.getSize() }}
                      key={header.id}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </DataTableHead>
                  ))}
                </DataTableRow>
              ))}
            </DataTableHeader>
            <DataTableBody>
              {table.getRowModel().rows.map((row) => (
                <DataTableRow
                  key={row.id}
                  data-testid={row.id}
                  onClick={() =>
                    dataLoadingGuard(row.original, (row) =>
                      handleClickRow(row.product.id),
                    )
                  }
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <DataTableCell key={cell.id} className="h-16">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </DataTableCell>
                  ))}
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
          {shouldDisplayNoData && (
            <div className="grow">
              <NoDataMessage
                title={common('no-table-data-title')}
                subtitle={common('no-table-data-subtitle')}
              />
            </div>
          )}
        </DataTableContainerContent>
        <DataTableContainerFooter>
          <DataTablePagination
            totalRecords={totalRows}
            onGetNextPage={getNextPage}
            onGetPreviousPage={getPrevPage}
            canGetNextPage={canGetNextPage}
            canGetPreviousPage={canGetPrevPage}
            currentPageIndex={pageIndex}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            currPageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
        </DataTableContainerFooter>
      </DataTableContainer>
    </AssortmentTableContextProvider>
  );
};
